import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { adminApi, type TenantUser } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { ArrowLeft, LogIn, Users, Shield, Calendar, Globe } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/ui/toast';
import styles from './TenantDetailPage.module.css';

export function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/_auth/tenant/$tenantId' });
  const [impersonating, setImpersonating] = useState(false);

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['admin', 'tenants', tenantId],
    queryFn: () => adminApi.tenants.get(tenantId),
  });

  const PORTAL_URL = (import.meta.env as Record<string, string>).VITE_PORTAL_URL ?? 'http://localhost:3002';

  async function handleImpersonate() {
    // Open blank tab synchronously inside the user gesture to avoid popup blockers
    const newTab = window.open('about:blank', '_blank');
    setImpersonating(true);
    try {
      const result = await adminApi.tenants.impersonate(tenantId);
      const url = `${PORTAL_URL}/auth/impersonate?token=${encodeURIComponent(result.token)}`;
      if (newTab) {
        newTab.location.href = url;
      } else {
        // Fallback if popup was blocked
        window.location.href = url;
      }
    } catch (err) {
      newTab?.close();
      toast.networkError(err, 'Impersonation');
      setImpersonating(false);
    } finally {
      setImpersonating(false);
    }
  }

  if (isLoading) return <div className={styles.loading}><Spinner size="lg" /></div>;
  if (!tenant) return (
    <div style={{ padding: '60px 0', textAlign: 'center', color: '#475569' }}>
      <p style={{ margin: 0, fontSize: 14 }}>Tenant not found or API is offline.</p>
    </div>
  );

  const STATUS_COLOR: Record<string, string> = {
    ACTIVE: 'success', TRIAL: 'warning', SUSPENDED: 'error', ARCHIVED: 'muted',
  };
  const PLAN_COLOR: Record<string, string> = {
    TRIAL: 'muted', STARTER: 'cyan', PROFESSIONAL: 'primary', ENTERPRISE: 'success',
  };

  return (
    <div className={styles.root}>
      <Link to="/tenants" className={styles.back}>
        <ArrowLeft size={16} /> Back to tenants
      </Link>

      {/* ── Header ──────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.avatar}>{tenant.name[0].toUpperCase()}</div>
          <div>
            <h1 className={styles.title}>{tenant.name}</h1>
            <p className={styles.slug}>@{tenant.slug}</p>
          </div>
          <span className={`${styles.badge} ${styles[`badge_${STATUS_COLOR[tenant.status] ?? 'muted'}`]}`}>
            {tenant.status}
          </span>
          <span className={`${styles.badge} ${styles[`badge_${PLAN_COLOR[tenant.plan] ?? 'muted'}`]}`}>
            {tenant.plan}
          </span>
        </div>

        <button
          id="impersonate-btn"
          className={styles.impersonateBtn}
          onClick={handleImpersonate}
          disabled={impersonating}
        >
          {impersonating ? <Spinner size="sm" /> : <LogIn size={15} />}
          {impersonating ? 'Starting session…' : 'Login as tenant'}
        </button>
      </div>


      {/* ── Info cards ────────────────── */}
      <div className={styles.infoGrid}>
        <InfoCard icon={<Calendar size={16} />} label="Created" value={new Date(tenant.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
        <InfoCard icon={<Users size={16} />}    label="Users"   value={String(tenant.users.length)} />
        <InfoCard icon={<Globe size={16} />}    label="Domain"  value={tenant.customDomain ?? 'Not set'} />
        <InfoCard icon={<Shield size={16} />}   label="Firebase" value={tenant.firebaseProjectId ?? 'Not linked'} />
      </div>

      {/* ── Users table ───────────────── */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Users size={16} /> Users ({tenant.users.length})
        </h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Type</th>
                <th>Last login</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {tenant.users.map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className={styles.infoCard}>
      <span className={styles.infoIcon}>{icon}</span>
      <div>
        <p className={styles.infoLabel}>{label}</p>
        <p className={styles.infoValue}>{value}</p>
      </div>
    </div>
  );
}

function UserRow({ user }: { user: TenantUser }) {
  const ROLE_COLOR: Record<string, string> = {
    ADMIN: 'primary', OPERATOR: 'cyan', VIEWER: 'muted', DEVELOPER: 'success',
  };
  return (
    <tr className={styles.userRow}>
      <td>
        <div className={styles.userCell}>
          <div className={styles.userAvatar}>{(user.name ?? user.email)[0].toUpperCase()}</div>
          <div>
            <p className={styles.userName}>{user.name ?? '—'}</p>
            <p className={styles.userEmail}>{user.email}</p>
          </div>
        </div>
      </td>
      <td>
        <span className={`${styles.badge} ${styles[`badge_${ROLE_COLOR[user.role] ?? 'muted'}`]}`}>
          {user.role}
        </span>
      </td>
      <td>
        {user.isSupportAccount ? (
          <span className={`${styles.badge} ${styles.badge_warning}`}>Support</span>
        ) : (
          <span className={`${styles.badge} ${styles.badge_muted}`}>Regular</span>
        )}
      </td>
      <td className={styles.dateCell}>
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
      </td>
      <td className={styles.dateCell}>
        {new Date(user.createdAt).toLocaleDateString()}
      </td>
    </tr>
  );
}
